import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import EditIcon from '../../assets/icons/edit.svg?react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setEditMode, toggleEditMode } from '../../store/application/applicationSlice';
import type { ChangeSummary } from '../../hooks/useYearPlan';
import { selectCanWrite } from '../../store/permissions/permissionsSlice';
import { Button } from '../UI/base/Button';
import { DiscardChangesModal } from './DiscardChangesModal';
import { EditActions } from './EditActions';
import { ReviewChangesModal } from './ReviewChangesModal';
import styles from './GanttEditToolbar.module.css';

interface GanttEditToolbarProps {
  isLocked?: boolean;
  lockedByCurrentUser?: boolean;
  lockedByName?: string | null;
  changeSummary?: ChangeSummary | null;
  onAcquireLock?: () => Promise<boolean>;
  onDiscardAll?: () => void | Promise<void>;
  onAcceptAll?: () => void | Promise<void>;
}

export const GanttEditToolbar = ({
  isLocked,
  lockedByCurrentUser,
  lockedByName,
  changeSummary,
  onAcquireLock,
  onDiscardAll,
  onAcceptAll
}: GanttEditToolbarProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isEditMode = useAppSelector((state) => state.applicationSlice.isEditMode);
  const hasUnsavedChanges = useAppSelector((state) => state.applicationSlice.hasUnsavedChanges);
  const canWrite = useAppSelector(selectCanWrite);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const handleEditClick = async () => {
    if (onAcquireLock) {
      const acquired = await onAcquireLock();
      if (!acquired) return;
    }
    dispatch(setEditMode(true));
  };

  const handleAccept = async () => {
    if (hasUnsavedChanges) {
      setShowReviewModal(true);
      return;
    }
    try {
      await onAcceptAll?.();
      dispatch(toggleEditMode());
    } catch {
      // Error is handled by the caller; stay in edit mode
    }
  };

  const discardAndExit = () => {
    onDiscardAll?.();
    dispatch(toggleEditMode());
  };

  // Discarding is unrecoverable, so pending edits get a confirmation. With nothing pending there is
  // nothing to lose, and the dialog would only be in the way of leaving edit mode.
  const handleDiscard = () => {
    if (hasUnsavedChanges) {
      setShowDiscardModal(true);
      return;
    }
    discardAndExit();
  };

  const handleConfirmDiscard = () => {
    setShowDiscardModal(false);
    discardAndExit();
  };

  const handleConfirm = async () => {
    setShowReviewModal(false);
    try {
      await onAcceptAll?.();
      dispatch(toggleEditMode());
    } catch {
      // Error is handled by the caller; stay in edit mode
    }
  };

  return (
    <div className={`${styles.editToolbar}${isEditMode ? ` ${styles.editToolbarExpanded}` : ''}`}>
      {isEditMode ? (
        <span className={styles.editLabel}>{t('edit-plan')}</span>
      ) : canWrite ? (
        <Button
          type="primary"
          icon={<EditIcon className={styles.editIcon} />}
          disabled={isLocked && !lockedByCurrentUser}
          // Button is hidden once edit mode starts, so skip the auto-disable pending state.
          onClick={() => {
            void handleEditClick();
          }}
        >
          {t('edit-plan')}
        </Button>
      ) : null}
      {isLocked && !lockedByCurrentUser && lockedByName && !isEditMode && (
        <span className={styles.lockedLabel}>
          <Trans
            i18nKey="plan-locked-by"
            values={{ name: lockedByName }}
            components={{ bold: <span className={styles.lockedName} /> }}
          />
        </span>
      )}
      <EditActions visible={isEditMode} onDiscard={handleDiscard} onAccept={handleAccept} />
      {showDiscardModal && (
        <DiscardChangesModal
          onConfirm={handleConfirmDiscard}
          onClose={() => {
            setShowDiscardModal(false);
          }}
        />
      )}
      {showReviewModal && (
        <ReviewChangesModal
          changeSummary={changeSummary}
          onClose={() => {
            setShowReviewModal(false);
          }}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};
